// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { AlertIcon, Banner, Box, Modal } from '@iheartjane/reefer';
import type { RowSelectionState } from '@tanstack/react-table';
import type { ReactNode } from 'react';

import { ConfirmWrapperWithTracking } from '@jane/business-admin/components/ConfirmWrapperWithTracking';
import { ModalNames } from '@jane/business-admin/util';

const ModalHeaderWrapper = styled.div(({ theme }) => ({
  '& > [data-id="modal-header"]': {
    backgroundColor: theme.colors.brand.purple.main,
    color: theme.colors.text.inverse,
    '& h1': {
      color: 'inherit',
    },
  },
}));

// Keep the banner mounted but hidden when inactive so toggling it doesn't
// reflow the table below it.
const InvalidBannerWrapper = styled.div<{ $visible: boolean }>(
  ({ $visible }) => ({
    visibility: $visible ? 'visible' : 'hidden',
  })
);

export const BulkEditModal = ({
  headerButton,
  onClose,
  open,
  productTable,
  reserveInvalidErrorSpace = false,
  selectedProducts,
  showInvalidError = false,
}: {
  headerButton: ReactNode;
  onClose: () => void;
  open: boolean;
  productTable: ReactNode;
  reserveInvalidErrorSpace?: boolean;
  selectedProducts: RowSelectionState;
  showInvalidError?: boolean;
}) => {
  const isDirty = Object.values(selectedProducts).length > 0;

  return (
    <ConfirmWrapperWithTracking
      open={open}
      setOpen={onClose}
      hasChanges={isDirty}
      variant="full-screen"
      background="text-inverse"
      modalName={ModalNames.BulkEditProducts}
    >
      <>
        <ModalHeaderWrapper>
          <Modal.Header title="Select products" actions={<>{headerButton}</>} />
        </ModalHeaderWrapper>
        <Modal.Content padding={false}>
          {reserveInvalidErrorSpace || showInvalidError ? (
            <Box px={64} pt={24}>
              <InvalidBannerWrapper
                $visible={showInvalidError}
                aria-hidden={!showInvalidError}
              >
                <Banner
                  full
                  variant="info"
                  icon={<AlertIcon />}
                  label="Products with invalid data can only be archived in bulk"
                />
              </InvalidBannerWrapper>
            </Box>
          ) : null}
          <Box mt={40}>{productTable}</Box>
        </Modal.Content>
      </>
    </ConfirmWrapperWithTracking>
  );
};
